import type { DataMode } from '../../../dataMode'

/**
 * The confirmation model for the two destructive dev tools.
 *
 * Both dialogues state exactly which tables they touch. "Reset all data" has always been a
 * slightly frightening button whose scope was documented in a code comment and nowhere the person
 * pressing it would ever read; the dialogue is where that belongs.
 *
 * Live mode gets a typed token rather than a click, matching the server's own gate —
 * one click is too cheap for the one destructive tool that survives against real data.
 */

export interface ConfirmedAction {
  ok: boolean
  /** The typed token to send, when the mode requires one. */
  confirm?: string
}

const RESET_SCOPE =
  'Wipes predictions, bankers, chips, points, season history, league snapshots and the gameweek ' +
  'clock — for EVERY user and bot. Accounts, leagues and memberships survive, and so does the ' +
  'football data (use "Resync football" for that).'

const RESYNC_SCOPE =
  'Deletes the fixtures, results, standings and match detail we hold, plus their sync state, and ' +
  're-fetches them from the provider. Gameplay state and the provider request ledger are untouched.'

function typedToken(token: string, message: string): ConfirmedAction {
  const answer = window.prompt(`${message}\n\nType ${token} to confirm.`)
  return answer === token ? { ok: true, confirm: token } : { ok: false }
}

export function confirmReset(mode: DataMode | null): ConfirmedAction {
  if (mode === 'live') return typedToken('RESET', `Reset all data (LIVE). ${RESET_SCOPE}`)
  return { ok: window.confirm(`Reset all data? ${RESET_SCOPE}`) }
}

/**
 * The one tool that spends provider requests, so the confirmation says what it will
 * cost before it runs — beside today's usage, which is the number that decides
 * whether it is a good idea right now.
 */
export function confirmResync(
  mode: DataMode | null,
  cost: { estimatedRequests: number; requestsToday: number; dailyBudget: number }
): ConfirmedAction {
  const price =
    `Costs about ${cost.estimatedRequests} provider request(s). ` +
    `Used today: ${cost.requestsToday}/${cost.dailyBudget}.`
  const message = `Resync football data. ${RESYNC_SCOPE}\n\n${price}`
  if (mode === 'live') return typedToken('RESYNC', `${message}\n\nThis is LIVE.`)
  return { ok: window.confirm(`${message}\n\nContinue?`) }
}

/**
 * The factory reset. Both blast radii at once, so the confirmation states both —
 * and states what survives, because "does this delete the accounts" is the question that stops
 * somebody pressing it.
 *
 * The token is longer than either half's on purpose: it is the two of them plus a re-anchor of the
 * season, and typing it should take longer than deciding to.
 */
const RESET_EVERYTHING_SCOPE =
  `${RESET_SCOPE.replace(' and so does the football data (use "Resync football" for that).', '.')} ` +
  `${RESYNC_SCOPE.replace('Gameplay state and the provider request ledger are untouched.', '')}` +
  'Then it re-anchors the replica season so gameweek 1 kicks off shortly, and re-syncs. ' +
  'Surviving: accounts, leagues, memberships, rivalries, the provider request ledger, the admin ' +
  'audit log, and the provider team mapping.'

export function confirmResetEverything(
  mode: DataMode | null,
  cost: { estimatedRequests: number; requestsToday: number; dailyBudget: number }
): ConfirmedAction {
  const price =
    `Costs about ${cost.estimatedRequests} provider request(s). ` +
    `Used today: ${cost.requestsToday}/${cost.dailyBudget}.`
  const message = `Reset EVERYTHING. ${RESET_EVERYTHING_SCOPE}\n\n${price}`
  if (mode === 'live') return typedToken('RESET EVERYTHING', `${message}\n\nThis is LIVE.`)
  return { ok: window.confirm(`${message}\n\nContinue?`) }
}
